import styled from 'styled-components';
import {useSelector} from 'react-redux';

import {media} from '~/shared/theme/utils';
import {Link} from '~/shared/router';
import {getCityPageUrl, replaceLegacyUrlToken} from '~/shared/services/urlService';
import {getCityByCityId} from '~/shared/services/citiesHelpers';
import {capitalFirstLetterAfterSpace} from '~/shared/utils/general';
import {getLocalizationService} from '~/shared/services/localisationService';
import useChat from '~/shared/hooks/useChat';
import {smallRegular} from '~/shared/theme/typography';
import {selectUserData} from '~/shared/store/selectors';
import {ETHICS_HOTLINE_URL} from '~/shared/consts/commonConsts';
import {trackEvent} from '~/shared/services/analytics';
import store from '~/shared/store';
import {setCurrentModal} from '~/shared/store/actions';

const Text = styled.div<{linkColorOnHover?: string}>`
  ${smallRegular}
  color: ${({theme}) => theme.colors.surface};
  cursor: pointer;
  text-align: center;

  &:hover {
    text-decoration: underline;
    color: ${({theme, linkColorOnHover}) => linkColorOnHover || theme.colors.secondaryText};
  }

  ${media.minLargeMobile`
    text-align: unset;
  `}
`;

const ExternalLink = styled(Link)<{sideBySide?: boolean}>`
  ${smallRegular}
  color: ${({theme}) => theme.colors.surface};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: ${({theme}) => theme.colors.secondaryText};
  }
`;

const CityLink = styled(ExternalLink)`
  width: calc(50% - 2px);
`;

type FooterLinkAction = 'openChat' | 'deleteAccount';

const isFooterLinkAction = (action?: string): action is FooterLinkAction => !!action &&
  (action === 'openChat' || action === 'deleteAccount');

const actionMap: Record<FooterLinkAction, (cb: () => void) => void> = {
  openChat: (cb: () => void) => cb(),
  deleteAccount: () => {
    trackEvent('hasClickedDeleteAccount');

    store.dispatch(
      setCurrentModal('remove_account_modal', {
        hideCloseButton: true,
      }),
    );
  },
};

const handleClick = (action: string | undefined, openChat: () => void) => {
  if (!isFooterLinkAction(action)) {
    return;
  }
  actionMap[action]?.(openChat);
};

type UserShowFor = 'isIecCompany' | 'isGovCompany';

const isUserShowFor = (showFor?: string): showFor is UserShowFor => !!showFor &&
  (showFor === 'isIecCompany' || showFor === 'isGovCompany');

export type FooterLinkProps = {
  className?: string;
  showFor?: string;
  href?: string;
  target?: string;
  text?: string;
  cityId?: string;
  action?: string;
  linkColorOnHover?: string;
};

const FooterLink = ({className, showFor, href, target, text, cityId, action, linkColorOnHover}: FooterLinkProps) => {
  const {openChat} = useChat();
  const {t, currentLanguageKey} = getLocalizationService();

  const user = useSelector(selectUserData);

  if (isUserShowFor(showFor) && !(user && user[showFor])) {
    return null;
  }

  if (href) {
    const resolvedHref = href === '<ethicsHotline>'
      ? ETHICS_HOTLINE_URL[currentLanguageKey] || ETHICS_HOTLINE_URL.en
      : href;
    const link = replaceLegacyUrlToken(resolvedHref);

    return (
      <ExternalLink className={className} target={target || '_blank'} href={link} hard>
        <Text linkColorOnHover={linkColorOnHover}>{t(text || '')}</Text>
      </ExternalLink>
    );
  }

  if (cityId) {
    const city = getCityByCityId(cityId);
    const currentLanguageCityPageLink = getCityPageUrl({
      city,
      languageKey: currentLanguageKey,
    });
    const cityNameInCurrentLanguage = city.cityName[currentLanguageKey];

    return (
      <CityLink className={className} target={target || '_blank'} href={currentLanguageCityPageLink} hard>
        <Text linkColorOnHover={linkColorOnHover}>{capitalFirstLetterAfterSpace(cityNameInCurrentLanguage)}</Text>
      </CityLink>
    );
  }

  if (text === 'delete_account' && (!user || user?.isCompanyUser)) {
    return null;
  }

  return (
    <Text
      className={className}
      onClick={() => handleClick(action, openChat)}
      linkColorOnHover={linkColorOnHover}
    >
      {t(text || '')}
    </Text>
  );
};

export default FooterLink;
